import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Activity,
  BarChart3,
  CheckCircle,
  ChevronDown,
  ClipboardList,
  Edit3,
  Eye,
  FileText,
  Filter,
  Heart,
  MoreVertical,
  Play,
  Plus,
  Search,
  StickyNote,
  Users,
  Video,
} from 'lucide-react';
import type { AppPrompt, PromptAnalyticsRow } from './promptsV2Types';
import { hasPromptVideo, mediaUrl, promptStatusLabel } from './promptsV2Utils';
import { PromptVisibilityIndicator } from './PromptVisibilityIndicator';
import {
  filterTabFromLabel,
  filterTabLabel,
  formatCompactCount,
  formatTrendHint,
  healthLabel,
  PORTFOLIO_PRO_TIPS,
  promptPublishedLabel,
  promptTags,
  type PromptBreakdown,
  type PromptFilterTab,
  type PromptSortKey,
  type RecommendedAction,
} from './promptsPortfolioUtils';

function formatVideoDuration(seconds: number) {
  if (!Number.isFinite(seconds) || seconds <= 0) return '';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

function HealthRing({
  pct,
  size = 128,
  stroke = 10,
}: {
  pct: number;
  size?: number;
  stroke?: number;
}) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (Math.min(100, Math.max(0, pct)) / 100) * circ;
  const c = size / 2;

  return (
    <svg width={size} height={size} className="-rotate-90" aria-hidden>
      <circle cx={c} cy={c} r={r} fill="none" stroke="#ecfdf5" strokeWidth={stroke} />
      <circle
        cx={c}
        cy={c}
        r={r}
        fill="none"
        stroke="#047857"
        strokeWidth={stroke}
        strokeDasharray={circ}
        strokeDashoffset={offset}
        strokeLinecap="round"
      />
    </svg>
  );
}

function VideoThumbnail({ src, onPlay }: { src: string; onPlay: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [duration, setDuration] = useState('');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const syncDuration = () => setDuration(formatVideoDuration(video.duration));
    const showFrame = () => {
      if (video.duration > 0.2) {
        video.currentTime = 0.1;
      }
      setReady(true);
    };

    video.addEventListener('loadedmetadata', syncDuration);
    video.addEventListener('loadeddata', showFrame);
    if (video.readyState >= 2) showFrame();
    if (video.readyState >= 1) syncDuration();

    return () => {
      video.removeEventListener('loadedmetadata', syncDuration);
      video.removeEventListener('loadeddata', showFrame);
    };
  }, [src]);

  return (
    <div className="relative h-[120px] w-full shrink-0 overflow-hidden rounded-lg bg-gradient-to-br from-slate-200 to-slate-300 sm:w-[150px]">
      <video
        ref={videoRef}
        src={src}
        muted
        playsInline
        preload="metadata"
        className={`h-full w-full object-cover transition-opacity duration-300 ${
          ready ? 'opacity-100' : 'opacity-0'
        }`}
      />
      <div className="absolute inset-0 grid place-items-center bg-black/15">
        <button
          type="button"
          onClick={onPlay}
          className="grid h-9 w-9 place-items-center rounded-full bg-white/95 text-emerald-700 shadow-md"
          aria-label="Play video"
        >
          <Play size={18} fill="currentColor" />
        </button>
      </div>
      {duration ? (
        <span className="absolute bottom-2.5 left-2.5 rounded-md bg-black/75 px-2 py-0.5 text-[11px] font-semibold text-white">
          {duration}
        </span>
      ) : null}
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  note,
  ringPct,
}: {
  icon?: ReactNode;
  label: string;
  value: string;
  note: string;
  ringPct?: number;
}) {
  return (
    <div className="rounded-xl border border-slate-200/90 bg-white px-3 py-2.5 shadow-sm">
      <div className="flex items-center gap-2.5">
        <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
          {ringPct != null ? (
            <>
              <HealthRing pct={ringPct} size={36} stroke={3} />
              <span className="absolute text-[8px] font-bold text-emerald-800">{ringPct}%</span>
            </>
          ) : (
            icon
          )}
        </div>
        <div className="min-w-0 leading-tight">
          {ringPct == null ? <p className="text-lg font-bold text-slate-950">{value}</p> : null}
          <p className="text-xs text-slate-500">{label}</p>
          <p className="text-[11px] font-semibold text-emerald-700">{note}</p>
        </div>
      </div>
    </div>
  );
}

function TagBadge({ tag }: { tag: string }) {
  const Icon = tag === 'Video' ? Video : tag === 'Document' ? FileText : StickyNote;
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-sky-200 bg-sky-50/80 px-2 py-0.5 text-[11px] font-medium text-sky-800">
      <Icon size={12} />
      {tag}
    </span>
  );
}

function PromptCardMenu({ onDelete }: { onDelete: () => void }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const closeOnOutside = (event: globalThis.MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        className="rounded-lg p-1 text-slate-400 hover:bg-slate-50"
        aria-label="More actions"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((value) => !value)}
      >
        <MoreVertical size={16} />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-1 min-w-[9rem] rounded-xl border border-slate-200 bg-white py-1 shadow-lg"
        >
          <button
            type="button"
            role="menuitem"
            className="block w-full px-3 py-2 text-left text-xs text-red-600 hover:bg-red-50"
            onClick={() => {
              setOpen(false);
              onDelete();
            }}
          >
            Delete prompt
          </button>
        </div>
      ) : null}
    </div>
  );
}

function ProTipsCard() {
  return (
    <div className="rounded-xl border border-slate-200/90 bg-gradient-to-b from-slate-50 to-white p-3 shadow-sm">
      <p className="text-xs font-bold uppercase tracking-wide text-slate-600">Pro tips</p>
      <ul className="mt-2 space-y-2">
        {PORTFOLIO_PRO_TIPS.map((tip) => (
          <li key={tip} className="flex gap-2 text-xs leading-snug text-slate-600">
            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-slate-400" aria-hidden />
            <span>{tip}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

type PromptCardProps = {
  prompt: AppPrompt;
  displayNumber: number;
  stats: PromptAnalyticsRow | null;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
};

function PromptCard({ prompt, displayNumber, stats, onView, onEdit, onDelete }: PromptCardProps) {
  const views = stats?.views ?? 0;
  const likes = stats?.likes ?? 0;
  const impressions = stats?.impressions ?? 0;
  const status = promptStatusLabel(prompt);
  const needsWork = status !== 'Complete';
  const tags = promptTags(prompt);
  const videoUrl = hasPromptVideo(prompt) ? mediaUrl(prompt.fileUrl) : '';
  const publishedLabel = promptPublishedLabel(prompt);

  return (
    <div className="rounded-xl border border-slate-200/90 bg-white px-3.5 py-3 shadow-sm transition hover:shadow-md">
      <div className="flex flex-col gap-3 sm:flex-row">
        {videoUrl ? (
          <VideoThumbnail src={videoUrl} onPlay={onView} />
        ) : (
          <button
            type="button"
            onClick={onEdit}
            className="flex h-[120px] w-full shrink-0 flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed border-slate-300 bg-slate-50 text-xs font-semibold text-slate-500 hover:bg-slate-100 sm:w-[150px]"
          >
            <Video size={18} className="text-emerald-700" />
            Add video
          </button>
        )}

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-start justify-between gap-2">
            <div className="flex min-w-0 gap-2.5">
              <span className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-emerald-700 text-xs font-bold text-white">
                {displayNumber}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-bold leading-snug text-slate-950">{prompt.content}</p>
                {tags.length > 0 ? (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {tags.map((tag) => (
                      <TagBadge key={tag} tag={tag} />
                    ))}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="flex shrink-0 flex-col items-end gap-1.5">
              <div className="flex items-center gap-1">
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${
                    needsWork ? 'bg-orange-50 text-orange-700' : 'bg-emerald-50 text-emerald-700'
                  }`}
                >
                  <CheckCircle size={12} />
                  {status}
                </span>
                <PromptCardMenu onDelete={onDelete} />
              </div>
              <span className="text-[11px] text-slate-400">{publishedLabel}</span>
            </div>
          </div>

          <div className="mt-2 border-t border-slate-100 pt-2">
            <div className="mb-2 flex justify-end">
              <PromptVisibilityIndicator
                visibility={prompt.visibility}
                itemLabel="screening prompt"
                compact
              />
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-2.5">
                <div className="flex items-center gap-1">
                  <span className="rounded-md bg-emerald-50 p-1 text-emerald-700">
                    <Eye size={12} />
                  </span>
                  <div>
                    <p className="text-xs font-bold text-slate-950">{views}</p>
                    <p className="text-[10px] text-slate-500">Views</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <span className="rounded-md bg-rose-50 p-1 text-rose-500">
                    <Heart size={12} />
                  </span>
                  <div>
                    <p className="text-xs font-bold text-slate-950">{likes}</p>
                    <p className="text-[10px] text-slate-500">Likes</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <span className="rounded-md bg-emerald-50 p-1 text-emerald-700">
                    <BarChart3 size={12} />
                  </span>
                  <div>
                    <p className="text-xs font-bold text-slate-950">
                      {formatCompactCount(impressions)}
                    </p>
                    <p className="text-[10px] text-slate-500">Impressions</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-1.5">
                <button
                  type="button"
                  onClick={onView}
                  className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 px-3 py-1.5 text-xs font-bold text-emerald-700 hover:bg-emerald-50"
                >
                  <Eye size={12} />
                  View
                </button>
                <button
                  type="button"
                  onClick={onEdit}
                  className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 px-3 py-1.5 text-xs font-bold text-emerald-700 hover:bg-emerald-50"
                >
                  <Edit3 size={12} />
                  Edit
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ActionItem({
  title,
  text,
  onClick,
}: {
  title: string;
  text: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-between rounded-lg bg-emerald-50 p-2.5 text-left transition hover:bg-emerald-100"
    >
      <div>
        <p className="text-sm font-bold text-emerald-800">{title}</p>
        <p className="text-xs text-slate-500">{text}</p>
      </div>
      <span className="text-lg text-emerald-700">›</span>
    </button>
  );
}

function BreakdownProgress({
  label,
  current,
  max,
}: {
  label: string;
  current: number;
  max: number;
}) {
  const pct = max > 0 ? Math.round((current / max) * 100) : 0;
  return (
    <div>
      <div className="mb-0.5 flex justify-between text-xs">
        <span className="text-slate-500">{label}</span>
        <span className="font-semibold text-slate-700">
          {current} / {max}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100">
        <div className="h-1.5 rounded-full bg-emerald-600" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export type PromptsPortfolioWorkspaceProps = {
  publishedCount: number;
  totalViews: number;
  recruitersInterested: number;
  promptHealthScore: number;
  publishedTrend: number;
  viewsTrend: number;
  recruitersTrend: number;
  filterTab: PromptFilterTab;
  sortKey: PromptSortKey;
  searchQuery: string;
  filterPanelOpen: boolean;
  onFilterTabChange: (tab: PromptFilterTab) => void;
  onSortChange: (sort: PromptSortKey) => void;
  onSearchChange: (value: string) => void;
  onToggleFilter: () => void;
  onAddPrompt: () => void;
  addDisabled: boolean;
  prompts: AppPrompt[];
  filteredPrompts: AppPrompt[];
  analyticsByPromptId: Map<number, PromptAnalyticsRow>;
  promptBreakdown: PromptBreakdown;
  healthScore: number;
  recommendedActions: RecommendedAction[];
  onView: (prompt: AppPrompt) => void;
  onEdit: (prompt: AppPrompt) => void;
  onDelete: (prompt: AppPrompt) => void;
  promptNumberById: Map<number, number>;
  onActionClick: (action: RecommendedAction) => void;
  renderPromptExtra?: (prompt: AppPrompt) => ReactNode;
};

const STATUS_FILTERS = ['All', 'Complete', 'Needs Work'] as const;

export function PromptsPortfolioWorkspace({
  publishedCount,
  totalViews,
  recruitersInterested,
  promptHealthScore,
  publishedTrend,
  viewsTrend,
  recruitersTrend,
  filterTab,
  sortKey,
  searchQuery,
  filterPanelOpen,
  onFilterTabChange,
  onSortChange,
  onSearchChange,
  onToggleFilter,
  onAddPrompt,
  addDisabled,
  prompts,
  filteredPrompts,
  analyticsByPromptId,
  promptBreakdown,
  healthScore,
  recommendedActions,
  onView,
  onEdit,
  onDelete,
  promptNumberById,
  onActionClick,
  renderPromptExtra,
}: PromptsPortfolioWorkspaceProps) {
  return (
    <div className="w-full max-w-[1400px] pb-2 pt-1">
      <div className="mb-2 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="mb-1 flex items-center gap-2">
            <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-emerald-700">
              Screening Responses
            </p>
            <span className="rounded-full bg-emerald-100 p-1 text-emerald-700">
              <Activity size={13} />
            </span>
          </div>
          <h1 className="text-[1.65rem] font-bold tracking-tight text-slate-950">
            Screening Prompts
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">
            Showcase your expertise through impactful screening responses that recruiters can view
            directly from your profile.
          </p>
        </div>
        <button
          type="button"
          onClick={onAddPrompt}
          disabled={addDisabled}
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-emerald-700/20 transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Plus size={17} />
          New Screening Response
        </button>
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_300px]">
        <div className="min-w-0">
          <div className="mb-3 grid gap-1.5 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard
              icon={<ClipboardList size={16} />}
              label="Published Prompts"
              value={String(publishedCount)}
              note={formatTrendHint(publishedTrend) ?? `${publishedCount} total`}
            />
            <StatCard
              icon={<Eye size={16} />}
              label="Total Views"
              value={String(totalViews)}
              note={formatTrendHint(viewsTrend) ?? 'Across all prompts'}
            />
            <StatCard
              icon={<Users size={16} />}
              label="Recruiters Interested"
              value={String(recruitersInterested)}
              note={formatTrendHint(recruitersTrend) ?? 'Unique profile viewers'}
            />
            <StatCard
              label="Prompt Health"
              value={`${promptHealthScore}%`}
              note={healthLabel(promptHealthScore)}
              ringPct={promptHealthScore}
            />
          </div>

          <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              {STATUS_FILTERS.map((item) => {
                const active = filterTabLabel(filterTab) === item;
                return (
                  <button
                    key={item}
                    type="button"
                    onClick={() => onFilterTabChange(filterTabFromLabel(item))}
                    className={`rounded-xl px-3.5 py-2 text-sm font-semibold transition ${
                      active && item === 'All'
                        ? 'bg-emerald-700 text-white shadow-sm'
                        : active
                          ? 'border border-emerald-300 bg-emerald-50 text-emerald-800'
                          : item === 'Needs Work'
                            ? 'border border-orange-200 bg-orange-50 text-orange-700'
                            : 'border border-emerald-100 bg-white text-emerald-700 hover:bg-emerald-50'
                    }`}
                  >
                    {item}
                  </button>
                );
              })}

              <div className="relative">
                <select
                  value={sortKey}
                  onChange={(e) => onSortChange(e.target.value as PromptSortKey)}
                  className="h-10 appearance-none rounded-xl border border-slate-200 bg-white pl-3.5 pr-9 text-sm font-medium text-slate-700 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                  aria-label="Sort prompts"
                >
                  <option value="order">Prompt order</option>
                  <option value="recent">Recently updated</option>
                  <option value="views">Most viewed</option>
                </select>
                <ChevronDown
                  size={15}
                  className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <div className="relative min-w-0 flex-1 lg:w-56">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="search"
                  placeholder="Search prompts..."
                  value={searchQuery}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className="h-10 w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </div>
              <button
                type="button"
                onClick={onToggleFilter}
                className={`inline-flex h-10 shrink-0 items-center gap-1.5 rounded-xl border px-3.5 text-sm font-semibold transition ${
                  filterPanelOpen
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Filter size={15} />
                Filter
              </button>
            </div>
          </div>

          {filterPanelOpen ? (
            <p className="mb-3 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs text-slate-500">
              Filter by completion status or sort by views and recency. Search matches prompt
              questions and notes.
            </p>
          ) : null}

          <div className="space-y-3">
            {prompts.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
                <p className="text-base font-bold text-slate-950">No prompt responses yet</p>
                <p className="mt-1 text-sm text-slate-500">
                  Use New Screening Response to build your first prompt set.
                </p>
              </div>
            ) : filteredPrompts.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
                <p className="text-base font-bold text-slate-950">No prompts match your filters</p>
                <p className="mt-1 text-sm text-slate-500">Try a different tab or search term.</p>
              </div>
            ) : (
              filteredPrompts.map((prompt) => {
                const displayNumber = promptNumberById.get(prompt.id) ?? prompt.id;
                return (
                  <div key={prompt.id} id={`prompt-card-${prompt.id}`}>
                    {renderPromptExtra?.(prompt)}
                    <PromptCard
                      prompt={prompt}
                      displayNumber={displayNumber}
                      stats={analyticsByPromptId.get(prompt.id) ?? null}
                      onView={() => onView(prompt)}
                      onEdit={() => onEdit(prompt)}
                      onDelete={() => onDelete(prompt)}
                    />
                  </div>
                );
              })
            )}
          </div>
        </div>

        <aside className="space-y-3">
          <div className="rounded-xl border border-slate-200/90 bg-white p-3 shadow-sm">
            <h3 className="text-sm font-bold text-slate-950">Prompt Health</h3>
            <div className="my-3 flex flex-col items-center">
              <div className="relative grid place-items-center">
                <HealthRing pct={healthScore} size={96} stroke={8} />
                <span className="absolute text-2xl font-bold text-slate-950">{healthScore}%</span>
              </div>
              <p className="mt-2 text-sm font-bold text-emerald-700">{healthLabel(healthScore)}</p>
              <p className="mt-0.5 max-w-[220px] text-center text-[11px] leading-snug text-slate-500">
                {healthScore >= 70
                  ? "You're doing great. Keep completing your prompts."
                  : 'Complete videos, documents, and notes to strengthen your set.'}
              </p>
            </div>
            <div className="space-y-2 border-t border-slate-100 pt-2.5">
              <BreakdownProgress
                label="Prompts"
                current={promptBreakdown.prompts.current}
                max={promptBreakdown.prompts.max}
              />
              <BreakdownProgress
                label="Videos"
                current={promptBreakdown.videos.current}
                max={promptBreakdown.videos.max}
              />
              <BreakdownProgress
                label="Documents"
                current={promptBreakdown.documents.current}
                max={promptBreakdown.documents.max}
              />
              <BreakdownProgress
                label="Notes"
                current={promptBreakdown.notes.current}
                max={promptBreakdown.notes.max}
              />
            </div>
          </div>

          <div className="rounded-xl border border-slate-200/90 bg-white p-3 shadow-sm">
            <h3 className="mb-2 text-sm font-bold text-slate-950">Recommended Actions</h3>
            <div className="space-y-2">
              {recommendedActions.map((action) => (
                <ActionItem
                  key={action.id}
                  title={action.label}
                  text={action.detail}
                  onClick={() => onActionClick(action)}
                />
              ))}
            </div>
          </div>

          <ProTipsCard />
        </aside>
      </div>
    </div>
  );
}
